<script setup>
/* eslint-disable vue/custom-event-name-casing */
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import conversationAPI from '../../../../api/inbox/conversation';
import PipelineAPI from '../../../../api/pipelineCards';
import dealStatusAPI from '../../../../api/pipeline/dealStatus';
import AttributeAPI from '../../../../api/attributes';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { useRouter } from 'vue-router';
import Modal from 'dashboard/components/Modal.vue';
import SendMessageTemplate from './SendMessageTemplate.vue';
import PipelineCardHero from './card-detail/PipelineCardHero.vue';
import PipelineStageStepper from 'dashboard/components-next/pipeline/PipelineStageStepper.vue';
import CardInfoPanel from './card-detail/CardInfoPanel.vue';
import CardAssigneePanel from './card-detail/CardAssigneePanel.vue';
import MarkAsWonModal from './MarkAsWonModal.vue';
import MarkAsLostModal from './MarkAsLostModal.vue';
import AttachmentsTab from './card-detail/tabs/AttachmentsTab.vue';
import ChecklistTab from './card-detail/tabs/ChecklistTab.vue';
import NotesTab from './card-detail/tabs/NotesTab.vue';
import ScheduledMessagesTab from './card-detail/tabs/ScheduledMessagesTab.vue';
import RelatedItemsTab from './card-detail/tabs/RelatedItemsTab.vue';
import LeadScoreOverrideModal from './LeadScoreOverrideModal.vue';
import contactAPI from '../../../../api/contacts';

// Definição dos props e emits
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'item-updated', 'deleted']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const activeTab = ref('notes');

// Refs para modais e menus
const showQuickActionsMenu = ref(false);
const showMessageModal = ref(false);

// Adicione estas refs
const customAttributes = ref([]);
const contactCustomAttributes = ref([]);

// Checklist state (kept in parent for Statistics card)
const checklistItems = ref(props.item.item_details?.checklist || []);

// Contact avatar URL for header (same logic as PipelineCard.vue)
const contactAvatarUrl = computed(() => {
  const itemDetails = props.item?.item_details;
  if (itemDetails?.contact_thumbnail && itemDetails.contact_thumbnail.trim()) {
    return itemDetails.contact_thumbnail;
  }
  const conversationContact = itemDetails?.conversation?.contact;
  if (conversationContact?.thumbnail && conversationContact.thumbnail.trim()) {
    return conversationContact.thumbnail;
  }
  if (
    conversationContact?.avatar_url &&
    conversationContact.avatar_url.trim()
  ) {
    return conversationContact.avatar_url;
  }
  if (props.item?.contact?.thumbnail && props.item.contact.thumbnail.trim()) {
    return props.item.contact.thumbnail;
  }
  return '';
});

// Contact name for header subtitle (only shown when different from title)
const contactName = computed(() => {
  return props.item?.contact?.name || props.item?.item_details?.company || '';
});

const cardTitle = computed(() => {
  return (
    props.item?.title ||
    props.item?.item_details?.title ||
    t('PIPELINE.UNTITLED_ITEM')
  );
});

const showSubtitle = computed(() => {
  return contactName.value && contactName.value !== cardTitle.value;
});

// Ref para armazenar os dados da conversa
const conversationData = ref(props.item.item_details?.conversation || null);

watch(
  () => props.item.item_details?.conversation,
  val => {
    conversationData.value = val || null;
  },
  { immediate: true }
);

// Whether a conversation is linked (for direct link button)
const hasLinkedConversation = computed(() => {
  return !!(
    conversationData.value || props.item?.item_details?.conversation_id
  );
});

// Resolve um stage ID para nome de exibição usando o pipeline selecionado
const resolveStageName = stageId => {
  if (!stageId) return '-';
  const currentPipeline = store.getters['pipelines/getSelectedPipeline'];
  if (!currentPipeline?.stages) return stageId;
  return currentPipeline.stages[stageId]?.name || stageId;
};

// Computed para obter o nome do estágio atual
const currentStageName = computed(() => {
  if (!props.item.pipeline_stage) return '';
  return resolveStageName(props.item.pipeline_stage);
});

// ── Stepper: ordered stages from the selected pipeline (dynamic, never hardcoded) ─
const orderedStages = computed(() => {
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  if (!pipeline?.stages) return [];
  return Object.entries(pipeline.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      position: stage.position ?? 0,
      color: stage.color,
      // Backend stores per-stage flags `is_won_stage` / `is_lost_stage`
      // (see Pipeline.stages JSONB). Frontend stepper uses `terminal` to
      // decide whether clicking opens the won/lost modal vs moving directly.
      // Without this OR, "Mover para Ganho/Perdido" silently moved without
      // capturing won_value / lost_reason.
      terminal:
        stage.terminal === true ||
        stage.is_won_stage === true ||
        stage.is_lost_stage === true,
    }))
    .sort((a, b) => a.position - b.position);
});

// ── Assignee panel data ────────────────────────────────────────────────────────
const accountAgents = computed(() => store.getters['agents/getAgents'] || []);

const assignedAgentIds = computed(() => {
  const owner = props.item?.owner;
  return owner ? [owner.id] : [];
});

// ── Stage changes count (for CardInfoPanel statistics) ────────────────────────
const stageChangesCount = computed(() => {
  return (props.item?.activities || []).filter(
    a => a.activity_type === 'stage_changed'
  ).length;
});

// ── Move loading state ────────────────────────────────────────────────────────
const isLoadingMove = ref(false);

// ── Won / Lost modal visibility (declared early — used in handleRequestTerminalMove below) ──
const showWonModal = ref(false);
const showLostModal = ref(false);
const showLeadScoreModal = ref(false);

// ── Stepper handlers ──────────────────────────────────────────────────────────
const handleMoveToStage = async newStageId => {
  try {
    isLoadingMove.value = true;
    await PipelineAPI.moveToStage(
      props.item.id,
      newStageId,
      props.item.pipeline_stage
    );
    emit('item-updated');
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.STAGE_UPDATED_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    isLoadingMove.value = false;
  }
};

const handleRequestTerminalMove = stageId => {
  const stage = orderedStages.value.find(s => s.id === stageId);
  if (!stage) return;
  const nameNorm = (stage.name || '').toLowerCase();
  if (nameNorm.includes('ganho') || nameNorm.includes('won')) {
    showWonModal.value = true;
  } else if (nameNorm.includes('perdido') || nameNorm.includes('lost')) {
    showLostModal.value = true;
  } else {
    handleMoveToStage(stageId);
  }
};

// ── Assignee handler ──────────────────────────────────────────────────────────
const handleAssignAgents = async newAgentIds => {
  try {
    const accountId = store.getters.getCurrentAccountId;
    const ownerId = newAgentIds[0] || null;
    await PipelineAPI.assignOwner(accountId, props.item.id, {
      user_id: ownerId,
    });
    emit('item-updated');
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_SAVE_ERROR'),
      action: { type: 'error' },
    });
  }
};

// ── Panel field update handler ────────────────────────────────────────────────
const handlePanelFieldUpdate = async (fieldName, newValue) => {
  const updateData = {};
  if (fieldName.startsWith('custom_attributes.')) {
    const key = fieldName.replace('custom_attributes.', '');
    const current = { ...(props.item.custom_attributes || {}) };
    current[key] = newValue;
    updateData.custom_attributes = current;
  } else {
    updateData[fieldName] = newValue;
  }
  try {
    await PipelineAPI.updateCard(props.item.id, updateData);
    emit('item-updated');
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_UPDATED_SUCCESS'),
      action: { type: 'success' },
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_SAVE_ERROR'),
      action: { type: 'error' },
    });
  }
};

// Adicione este computed para pegar o nome do usuário atual
const currentUser = computed(() => store.getters.getCurrentUser);

// Check if current user is admin
const isAdmin = computed(() => currentUser.value?.role === 'administrator');

// Delete confirmation modal
const showDeleteConfirmModal = ref(false);
const isDeleting = ref(false);

// Adicione esta função para buscar os atributos disponíveis
const fetchCustomAttributes = async () => {
  try {
    const response = await AttributeAPI.getAttributesByModel();
    customAttributes.value = response.data.filter(
      attr => attr.attribute_model === 'pipeline_card_attribute'
    );
    contactCustomAttributes.value = response.data.filter(
      attr => attr.attribute_model === 'contact_attribute'
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[PipelineCardDetails] fetchCustomAttributes failed:', error);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.LOAD_ATTRIBUTES_ERROR'),
      action: { type: 'error' },
    });
  }
};

// Função para buscar dados da conversa
const fetchConversationData = async conversationId => {
  if (!conversationId) return;

  try {
    const response = await conversationAPI.get({
      assigneeType: ['all'],
      status: ['open', 'pending', 'resolved'],
    });
    const convPayload = response.data.data.payload;
    conversationData.value = convPayload.find(c => c.id === conversationId);
  } catch (error) {
    // Error handled silently
  }
};

// Notes state (kept in parent for Statistics card and passed to NotesTab/AttachmentsTab)
const notes = ref([]);

// Watch para manter notes sincronizado com as mudanças do item
watch(
  () => props.item.item_details?.notes,
  newNotes => {
    if (newNotes) {
      notes.value = [...newNotes].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
    }
  },
  { immediate: true }
);

// ── Attachments count (mirrors AttachmentsTab.vue logic) ─────────────────────
// Declared after `notes` ref to satisfy no-use-before-define.
const attachmentsCount = computed(() => {
  const noteAttachments = notes.value.reduce((acc, note) => {
    return (
      acc + (Array.isArray(note.attachments) ? note.attachments.length : 0)
    );
  }, 0);
  const itemDetailsCount = props.item?.item_details?.attachments?.length || 0;
  const activeStorageCount = props.item?.attachments?.length || 0;

  // Deduplicate by id (same logic as getAllAttachments in AttachmentsTab)
  const allRaw = [
    ...notes.value.flatMap(n =>
      Array.isArray(n.attachments) ? n.attachments : []
    ),
    ...(props.item?.item_details?.attachments || []),
    ...(props.item?.attachments || []),
  ];
  const seen = new Set();
  const unique = allRaw.filter(att => {
    const key = att?.id?.toString();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return (
    unique.length || noteAttachments + itemDetailsCount + activeStorageCount
  );
});

const showMoveModal = ref(false);

// ── Keyboard shortcuts ────────────────────────────────────────────────────────
// Mobile drawer toggles (hidden on lg+ screens)
const showLeftDrawer = ref(false);
const showRightDrawer = ref(false);
// Related items drawer (triggered from header button)
const showRelatedDrawer = ref(false);

const handleKeydown = event => {
  // Ignore when user is typing in a form field
  const tag = event.target?.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || event.target?.isContentEditable)
    return;
  // Ignore with modifiers
  if (event.ctrlKey || event.metaKey || event.altKey) return;

  switch (event.key) {
    case 'e':
      event.preventDefault();
      window.dispatchEvent(new CustomEvent('pipeline-card:focus-title'));
      break;
    case 's':
      event.preventDefault();
      window.dispatchEvent(new CustomEvent('pipeline-card:focus-status'));
      break;
    case 'n':
      event.preventDefault();
      activeTab.value = 'notes';
      nextTick(() => {
        const textarea = document.querySelector('[data-pipeline-notes-input]');
        textarea?.focus();
      });
      break;
    default:
      break;
  }
};

onMounted(() => {
  fetchCustomAttributes();

  // Verificar se a conversa já está nos detalhes
  if (
    !props.item.item_details?.conversation &&
    props.item.item_details?.conversation_id
  ) {
    fetchConversationData(props.item.item_details.conversation_id);
  }

  document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
});

// Adicione uma função para navegar para uma conversa
const navigateToConversation = () => {
  // Usar display_id da conversa vinculada ao card
  const displayId =
    conversationData.value?.display_id || props.item.conversation_display_id;

  if (!displayId) {
    return;
  }

  // Obter o account_id de forma mais segura
  const accountId = store.getters.getCurrentAccountId;

  // Fechar o menu de ações rápidas
  showQuickActionsMenu.value = false;

  try {
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId,
        conversation_id: displayId,
      },
    });
  } catch (error) {
    // Fallback: navegação direta pela URL com account_id atual
    window.location.href = `/app/accounts/${accountId}/conversations/${displayId}`;
  }
};

// Navigate to another pipeline card from RelatedItemsTab
const handleNavigateToCard = cardId => {
  const accountId = store.getters.getCurrentAccountId;
  try {
    router.push({
      name: 'pipeline_item_detail',
      params: { accountId, itemId: cardId },
    });
  } catch {
    window.location.href = `/app/accounts/${accountId}/pipeline/items/${cardId}`;
  }
};

// Adicione ao script
const tabs = [
  { id: 'notes', icon: 'document', label: t('PIPELINE.TABS.NOTES') },
  {
    id: 'checklist',
    icon: 'checkmark-circle',
    label: t('PIPELINE.TABS.CHECKLIST'),
  },
  { id: 'attachments', icon: 'attach', label: t('PIPELINE.TABS.ATTACHMENTS') },
  {
    id: 'scheduled',
    icon: 'calendar-clock',
    label: t('PIPELINE.TABS.SCHEDULED_MESSAGES'),
  },
];

// ── Inline title update ───────────────────────────────────────────────────────
const handleTitleUpdate = async newTitle => {
  try {
    await PipelineAPI.updateCard(props.item.id, { title: newTitle });
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_UPDATED_SUCCESS'),
      action: { type: 'success' },
    });
    emit('item-updated');
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_SAVE_ERROR'),
      action: { type: 'error' },
    });
  }
};

// ── Inline priority update ────────────────────────────────────────────────────
// Send ONLY the delta — backend does deep_merge from the DB row, so spreading
// `...props.item.item_details` (which can be stale) would resurrect old
// values for keys like `checklist` that were just modified by another tab.
const handlePriorityUpdate = async newPriority => {
  try {
    await PipelineAPI.updateCard(props.item.id, {
      item_details: { priority: newPriority },
    });
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_UPDATED_SUCCESS'),
      action: { type: 'success' },
    });
    emit('item-updated');
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_SAVE_ERROR'),
      action: { type: 'error' },
    });
  }
};

// ── Status change handlers (from Hero pills) ──────────────────────────────────
const handleRequestMarkWon = () => {
  showWonModal.value = true;
};

const handleRequestMarkLost = () => {
  showLostModal.value = true;
};

const handleRequestReopen = async () => {
  try {
    // The reopen endpoint returns the authoritative deal-status payload — pass
    // it up so the open card + board update instantly (no stale "Ganho/Perdido"
    // window while a follow-up GET is in flight).
    const { data } = await dealStatusAPI.reopen(props.item.id);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.CARD.STATUS.REOPENED_SUCCESS'),
      action: { type: 'success' },
    });
    emit('item-updated', data);
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.CARD.STATUS.REOPENED_ERROR'),
      action: { type: 'error' },
    });
  }
};

const handleWonSuccess = payload => {
  showWonModal.value = false;
  emit('item-updated', payload);
};

const handleLostSuccess = payload => {
  showLostModal.value = false;
  emit('item-updated', payload);
};

// Função para duplicar o item
const handleDuplicateItem = async () => {
  try {
    const duplicatedItem = {
      pipeline_id: props.item.pipeline_id,
      pipeline_stage: props.item.pipeline_stage,
      contact_id: props.item.contact_id,
      owner_id: props.item.owner_id,
      company_id: props.item.company_id,
      lead_score: props.item.lead_score,
      qualification_status: props.item.qualification_status,
      source: props.item.source,
      probability: props.item.probability,
      scheduled_at: props.item.scheduled_at,
      deadline: props.item.deadline,
      item_details: {
        ...props.item.item_details,
        title: `${props.item.item_details.title} (Cópia)`,
      },
    };

    await PipelineAPI.createCard(duplicatedItem);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_DUPLICATED_SUCCESS'),
      action: { type: 'success' },
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_DUPLICATED_ERROR'),
      action: { type: 'error' },
    });
  }
};

// Função para lidar com ações rápidas
const handleQuickAction = actionId => {
  switch (actionId) {
    case 'move':
      showMoveModal.value = true;
      break;
    case 'assign': {
      const accountId = store.getters.getCurrentAccountId;
      const contactId = props.item.item_details?.conversation?.contact?.id;

      if (!contactId) {
        return;
      }

      const routeConfig = {
        name: 'contacts_show',
        params: {
          accountId,
          id: contactId,
        },
      };

      try {
        router.push(routeConfig);
      } catch (err) {
        const fallbackUrl = `/app/accounts/${accountId}/contacts/${contactId}`;
        window.location.href = fallbackUrl;
      }
      break;
    }
    case 'chat':
    case 'send_message':
      showMessageModal.value = true;
      break;
    case 'duplicate':
      handleDuplicateItem();
      break;
    default:
      break;
  }
};

// Handle message sent
const handleMessageSent = () => {
  showMessageModal.value = false;
  // Optionally refresh item data
};

// ── Unlink conversation ───────────────────────────────────────────────────────
// CardInfoPanel já exibe window.confirm antes de emitir — basta executar o update.
const handleUnlinkConversation = async () => {
  try {
    await PipelineAPI.updateCard(props.item.id, {
      conversation_display_id: null,
      item_details: {
        ...props.item.item_details,
        conversation_id: null,
        conversation: null,
      },
    });
    emit('item-updated');
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CONVERSATION_UNLINKED'),
      action: { type: 'success' },
    });
  } catch {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CONVERSATION_UNLINK_ERROR'),
      action: { type: 'error' },
    });
  }
};

// ── Link conversation modal ───────────────────────────────────────────────────
const showLinkConversationModal = ref(false);
const linkConversationList = ref([]);
const linkConversationLoading = ref(false);
const selectedConversationId = ref(null);

const handleLinkConversation = async () => {
  const contactId =
    props.item?.contact_id ||
    props.item?.contact?.id ||
    props.item?.item_details?.conversation?.contact?.id ||
    null;

  if (!contactId) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.NO_CONTACT_TO_LINK'),
      action: { type: 'warning' },
    });
    return;
  }

  showLinkConversationModal.value = true;
  selectedConversationId.value = null;
  linkConversationLoading.value = true;
  try {
    const response = await contactAPI.getConversations(contactId);
    linkConversationList.value = response.data?.payload || [];
  } catch {
    linkConversationList.value = [];
  } finally {
    linkConversationLoading.value = false;
  }
};

const confirmLinkConversation = async () => {
  if (!selectedConversationId.value) return;
  const conv = linkConversationList.value.find(
    c => c.id === selectedConversationId.value
  );
  if (!conv) return;
  try {
    await PipelineAPI.updateCard(props.item.id, {
      conversation_display_id: conv.display_id,
    });
    showLinkConversationModal.value = false;
    emit('item-updated');
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CONVERSATION_LINKED'),
      action: { type: 'success' },
    });
  } catch {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CONVERSATION_LINK_ERROR'),
      action: { type: 'error' },
    });
  }
};

// Handle delete card
const handleDeleteCard = async () => {
  try {
    isDeleting.value = true;
    await PipelineAPI.deleteCard(props.item.id);
    showDeleteConfirmModal.value = false;
    showQuickActionsMenu.value = false;
    emit('deleted');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[PipelineCardDetails] handleDeleteCard failed:', error);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.ITEM_DELETED_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    isDeleting.value = false;
  }
};

const openDeleteConfirmModal = () => {
  showQuickActionsMenu.value = false;
  showDeleteConfirmModal.value = true;
};

// Computed properties for statistics card - REMOVED, MOVED TO PipelineItemStatistics.vue
// const itemStatistics = computed(() => {
//   const timeInStage = props.item.stage_entered_at
//     ? formatDistanceToNow(new Date(props.item.stage_entered_at), { locale: ptBR, addSuffix: false })
//     : t('PIPELINE.STATISTICS.NOT_AVAILABLE');
//
//   const noteCount = notes.value?.length || 0;
//   const checklistProgressText = `${completedItems.value}/${totalItems.value}`;
//   const attachmentCount = getAllAttachments.value?.length || 0;
//
//   return [
//     { icon: 'book-clock', label: t('PIPELINE.STATISTICS.TIME_IN_STAGE'), value: timeInStage },
//     { icon: 'comment-add', label: t('PIPELINE.STATISTICS.NOTES'), value: noteCount },
//     { icon: 'checkmark-circle', label: t('PIPELINE.STATISTICS.CHECKLIST'), value: checklistProgressText },
//     { icon: 'attach', label: t('PIPELINE.STATISTICS.ATTACHMENTS'), value: attachmentCount },
//   ];
// });
</script>

<template>
  <div class="flex flex-col h-full bg-n-background overflow-hidden">
    <!-- Header -->
    <PipelineCardHero
      :conversation-data="conversationData"
      :contact-avatar-url="contactAvatarUrl"
      :contact-name="contactName"
      :card-title="cardTitle"
      :show-subtitle="showSubtitle"
      :current-stage-name="currentStageName"
      :has-linked-conversation="hasLinkedConversation"
      :is-admin="isAdmin"
      :item="item"
      @close="$emit('close')"
      @navigate-to-conversation="navigateToConversation"
      @send-message="handleQuickAction('send_message')"
      @delete-card="openDeleteConfirmModal"
      @update-title="handleTitleUpdate"
      @update-priority="handlePriorityUpdate"
      @request-mark-won="handleRequestMarkWon"
      @request-mark-lost="handleRequestMarkLost"
      @request-reopen="handleRequestReopen"
      @open-related-items="showRelatedDrawer = true"
    />

    <!-- Stepper -->
    <div class="border-b border-n-weak bg-n-solid-2 px-6 py-3 flex-shrink-0">
      <PipelineStageStepper
        :stages="orderedStages"
        :current-stage-id="item.pipeline_stage"
        :is-won="!!item.won_at"
        :is-lost="!!item.lost_at"
        :disabled="isLoadingMove"
        @move-to-stage="handleMoveToStage"
        @request-terminal-move="handleRequestTerminalMove"
      />
    </div>

    <!-- Mobile drawer toggle buttons (hidden on lg+) -->
    <div
      class="lg:hidden flex items-center gap-2 px-4 py-2 border-b border-n-weak bg-n-solid-1 flex-shrink-0"
    >
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-1 transition-colors"
        @click="showLeftDrawer = true"
      >
        <fluent-icon icon="info" size="14" />
        <span>{{ t('PIPELINE.SHORTCUTS.OPEN_DETAILS') }}</span>
      </button>
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-1 transition-colors"
        @click="showRightDrawer = true"
      >
        <fluent-icon icon="person" size="14" />
        <span>{{ t('PIPELINE.SHORTCUTS.OPEN_ASSIGNEES') }}</span>
      </button>
    </div>

    <!-- 3-column body -->
    <div class="flex-1 grid grid-cols-12 gap-4 px-4 py-4 overflow-hidden">
      <!-- Sidebar esquerda: CardInfoPanel (desktop only) -->
      <CardInfoPanel
        class="hidden lg:block col-span-3 overflow-y-auto"
        :item="item"
        :custom-attributes="customAttributes"
        :contact-custom-attributes="contactCustomAttributes"
        :notes-count="notes.length"
        :checklist-total="checklistItems.length"
        :checklist-completed="checklistItems.filter(c => c.completed).length"
        :attachments-count="attachmentsCount"
        :stage-changes-count="stageChangesCount"
        @go-to-tab="activeTab = $event"
        @update-field="handlePanelFieldUpdate"
        @link-conversation="handleLinkConversation"
        @unlink-conversation="handleUnlinkConversation"
        @override-lead-score="showLeadScoreModal = true"
        @item-updated="emit('item-updated')"
      />

      <!-- Centro: tabs + conteúdo -->
      <div
        class="col-span-12 lg:col-span-6 flex flex-col bg-n-solid-2 rounded-xl border border-n-weak overflow-hidden"
      >
        <!-- Tabs nav horizontal -->
        <div
          class="flex border-b border-n-weak bg-n-solid-1 overflow-x-auto flex-shrink-0"
        >
          <button
            v-for="tab in tabs"
            :key="tab.id"
            class="flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors"
            :class="
              activeTab === tab.id
                ? 'border-n-brand text-n-brand'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="activeTab = tab.id"
          >
            <fluent-icon :icon="tab.icon" size="16" />
            <span>{{ tab.label }}</span>
          </button>
        </div>

        <!-- Tab content -->
        <div class="flex-1 overflow-y-auto p-6">
          <!-- Tab: Notes -->
          <NotesTab
            v-if="activeTab === 'notes'"
            :item="item"
            :notes="notes"
            @item-updated="emit('item-updated')"
            @update:notes="notes = $event"
          />

          <!-- Tab: Checklist -->
          <ChecklistTab
            v-else-if="activeTab === 'checklist'"
            :item="item"
            :checklist-items="checklistItems"
            @item-updated="emit('item-updated')"
            @update:checklist-items="checklistItems = $event"
            @go-to-tab="activeTab = $event"
          />

          <!-- Tab: Attachments -->
          <AttachmentsTab
            v-else-if="activeTab === 'attachments'"
            :item="item"
            :notes="notes"
            @item-updated="emit('item-updated')"
            @go-to-tab="activeTab = $event"
          />

          <!-- Tab: Scheduled Messages (replaces legacy Sequences tab) -->
          <ScheduledMessagesTab
            v-else-if="activeTab === 'scheduled'"
            :item="item"
            @item-updated="emit('item-updated')"
          />
        </div>
      </div>

      <!-- Sidebar direita: CardAssigneePanel (desktop only) -->
      <CardAssigneePanel
        class="hidden lg:block col-span-3 overflow-y-auto"
        :item="item"
        :agents="accountAgents"
        :assigned-agent-ids="assignedAgentIds"
        @assign-agents="handleAssignAgents"
        @item-updated="emit('item-updated')"
      />
    </div>

    <!-- Mobile left drawer: CardInfoPanel -->
    <div
      v-if="showLeftDrawer"
      class="lg:hidden fixed inset-0 z-50 bg-black/50"
      @click.self="showLeftDrawer = false"
    >
      <div
        class="absolute inset-y-0 left-0 w-80 max-w-[85vw] overflow-y-auto bg-n-solid-1 shadow-xl"
      >
        <div
          class="flex items-center justify-between p-4 border-b border-n-weak"
        >
          <span class="text-sm font-medium text-n-slate-12">{{
            t('PIPELINE.SHORTCUTS.OPEN_DETAILS')
          }}</span>
          <button
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-10"
            @click="showLeftDrawer = false"
          >
            <fluent-icon icon="dismiss" size="16" />
          </button>
        </div>
        <div class="p-4">
          <CardInfoPanel
            :item="item"
            :custom-attributes="customAttributes"
            :contact-custom-attributes="contactCustomAttributes"
            :notes-count="notes.length"
            :checklist-total="checklistItems.length"
            :checklist-completed="
              checklistItems.filter(c => c.completed).length
            "
            :attachments-count="attachmentsCount"
            :stage-changes-count="stageChangesCount"
            @go-to-tab="
              activeTab = $event;
              showLeftDrawer = false;
            "
            @update-field="handlePanelFieldUpdate"
            @link-conversation="handleLinkConversation"
            @unlink-conversation="handleUnlinkConversation"
            @override-lead-score="showLeadScoreModal = true"
            @item-updated="emit('item-updated')"
          />
        </div>
      </div>
    </div>

    <!-- Mobile right drawer: CardAssigneePanel -->
    <div
      v-if="showRightDrawer"
      class="lg:hidden fixed inset-0 z-50 bg-black/50"
      @click.self="showRightDrawer = false"
    >
      <div
        class="absolute inset-y-0 right-0 w-80 max-w-[85vw] overflow-y-auto bg-n-solid-1 shadow-xl"
      >
        <div
          class="flex items-center justify-between p-4 border-b border-n-weak"
        >
          <span class="text-sm font-medium text-n-slate-12">{{
            t('PIPELINE.SHORTCUTS.OPEN_ASSIGNEES')
          }}</span>
          <button
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-10"
            @click="showRightDrawer = false"
          >
            <fluent-icon icon="dismiss" size="16" />
          </button>
        </div>
        <div class="p-4">
          <CardAssigneePanel
            :item="item"
            :agents="accountAgents"
            :assigned-agent-ids="assignedAgentIds"
            @assign-agents="handleAssignAgents"
            @item-updated="emit('item-updated')"
          />
        </div>
      </div>
    </div>

    <!-- Modals -->

    <!-- Link Conversation Modal -->
    <Modal
      v-model:show="showLinkConversationModal"
      :on-close="() => (showLinkConversationModal = false)"
    >
      <div class="flex flex-col gap-4 p-6 min-w-[320px] max-w-md">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('PIPELINE.PANELS.LINK_CONVERSATION_BTN') }}
        </h3>
        <div v-if="linkConversationLoading" class="text-sm text-n-slate-11">
          {{ t('PIPELINE.LOADING') }}
        </div>
        <div
          v-else-if="linkConversationList.length === 0"
          class="text-sm text-n-slate-11"
        >
          {{ t('PIPELINE.MESSAGES.NO_CONVERSATIONS_FOR_CONTACT') }}
        </div>
        <div v-else class="flex flex-col gap-2 max-h-64 overflow-y-auto">
          <label
            v-for="conv in linkConversationList"
            :key="conv.id"
            class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
            :class="
              selectedConversationId === conv.id
                ? 'border-n-brand bg-n-alpha-1'
                : 'border-n-weak hover:bg-n-alpha-1'
            "
          >
            <input
              v-model="selectedConversationId"
              type="radio"
              :value="conv.id"
              class="mt-0.5"
            />
            <div class="flex flex-col gap-0.5 min-w-0">
              <span class="text-sm font-medium text-n-slate-12">
                #{{ conv.display_id }}
              </span>
              <span class="text-xs text-n-slate-11 truncate">
                {{ conv.meta?.channel || conv.inbox_id }}
              </span>
            </div>
          </label>
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <button
            class="px-4 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
            @click="showLinkConversationModal = false"
          >
            {{ t('CANCEL') }}
          </button>
          <button
            class="px-4 py-2 text-sm rounded-lg bg-n-brand text-white hover:bg-n-brand-dark disabled:opacity-50"
            :disabled="!selectedConversationId"
            @click="confirmLinkConversation"
          >
            {{ t('PIPELINE.PANELS.LINK_CONVERSATION_BTN') }}
          </button>
        </div>
      </div>
    </Modal>

    <Modal
      v-model:show="showMessageModal"
      :on-close="() => (showMessageModal = false)"
    >
      <SendMessageTemplate
        :item="item"
        :conversation-id="conversationData?.id || item.conversation_display_id"
        :current-stage="item.pipeline_stage"
        :contact="conversationData?.contact || {}"
        :conversation="conversationData || {}"
        @close="showMessageModal = false"
        @sent="handleMessageSent"
      />
    </Modal>

    <!-- Related Items drawer (slides in from right, triggered from header) -->
    <div
      v-if="showRelatedDrawer"
      class="fixed inset-0 z-50 bg-black/50"
      @click.self="showRelatedDrawer = false"
    >
      <div
        class="absolute inset-y-0 right-0 w-full max-w-2xl bg-n-solid-1 shadow-2xl flex flex-col"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
        >
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('PIPELINE.RELATED.TITLE') }}
          </h2>
          <button
            type="button"
            class="p-2 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
            :title="t('GENERAL_SETTINGS.CLOSE')"
            @click="showRelatedDrawer = false"
          >
            <fluent-icon icon="dismiss" size="18" />
          </button>
        </div>
        <div class="flex-1 overflow-y-auto p-6">
          <RelatedItemsTab
            :item="item"
            @navigate-to-card="
              cardId => {
                showRelatedDrawer = false;
                handleNavigateToCard(cardId);
              }
            "
          />
        </div>
      </div>
    </div>

    <!-- Mark as Won Modal -->
    <MarkAsWonModal
      :item="item"
      :show="showWonModal"
      @close="showWonModal = false"
      @success="handleWonSuccess"
    />

    <!-- Mark as Lost Modal -->
    <MarkAsLostModal
      :item="item"
      :show="showLostModal"
      @close="showLostModal = false"
      @success="handleLostSuccess"
    />

    <!-- Lead Score Override Modal -->
    <LeadScoreOverrideModal
      :pipeline-card="item"
      :show="showLeadScoreModal"
      @close="showLeadScoreModal = false"
      @updated="emit('item-updated')"
    />

    <!-- Delete Confirmation Modal -->
    <Modal
      v-model:show="showDeleteConfirmModal"
      :on-close="() => (showDeleteConfirmModal = false)"
    >
      <div class="p-6 max-w-md mx-auto">
        <div class="flex items-center gap-3 mb-4">
          <div
            class="w-10 h-10 rounded-full bg-n-ruby-3 flex items-center justify-center"
          >
            <fluent-icon icon="delete" size="20" class="text-n-ruby-11" />
          </div>
          <h3 class="text-heading-2 text-n-slate-12">
            {{ t('PIPELINE.CARD.DELETE_CONFIRM_TITLE') }}
          </h3>
        </div>
        <p class="text-sm text-n-slate-11 mb-6">
          {{ t('PIPELINE.CARD.DELETE_CONFIRM_MESSAGE') }}
        </p>
        <div class="flex justify-end gap-3">
          <button
            class="px-4 py-2 text-sm font-medium text-n-slate-11 border border-n-weak hover:bg-n-alpha-1 rounded-lg transition-colors"
            :disabled="isDeleting"
            @click="showDeleteConfirmModal = false"
          >
            {{ t('PIPELINE.CARD.CANCEL') }}
          </button>
          <button
            class="px-4 py-2 text-sm font-medium text-white bg-n-ruby-9 hover:brightness-110 rounded-lg transition-colors disabled:opacity-50"
            :disabled="isDeleting"
            @click="handleDeleteCard"
          >
            <span v-if="isDeleting" class="flex items-center gap-2">
              <span
                class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
              />
              {{ t('PIPELINE.CARD.DELETING') }}
            </span>
            <span v-else>{{ t('PIPELINE.CARD.DELETE') }}</span>
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style scoped>
.pipeline-details {
  max-height: 90vh;
  overflow-y: auto;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

/* Oculta scrollbar para Chrome, Safari e Opera */
.pipeline-details::-webkit-scrollbar {
  display: none;
}

/* Oculta scrollbar para elementos com custom-scrollbar também */
.custom-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.custom-scrollbar::-webkit-scrollbar {
  display: none;
}

.header-section {
  @apply bg-n-solid-2
    rounded-lg
    p-4 mb-4
    border border-n-weak;
}

.info-grid {
  @apply grid gap-3 mt-3;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

/* Definição base do card */
.info-card {
  @apply relative overflow-hidden flex flex-col gap-1 p-3 rounded-lg transition-all duration-200
    bg-n-solid-2 border border-n-weak;
}

/* Cards com cores diferentes */
.info-card:nth-child(1) {
  @apply bg-n-alpha-1 border-n-weak;
}

.info-card:nth-child(2) {
  @apply bg-n-solid-3 border-n-weak;
}

.info-card:nth-child(3) {
  @apply bg-n-solid-3 border-n-weak;
}

.info-card:nth-child(4) {
  @apply bg-n-solid-3 border-n-weak;
}

.info-label {
  @apply text-xs font-semibold uppercase tracking-wide text-n-slate-11;
}

.info-content {
  @apply text-sm font-medium text-n-slate-12;
}

/* Hover e interações */
.info-card:hover {
  @apply transform -translate-y-0.5 shadow-sm;
}

.conversation-card {
  background-color: var(--color-background-light);
  border-radius: 0.25rem;
  padding: 0.375rem;
  margin-top: 0.125rem;
}

.conversation-title {
  font-size: 0.8125rem;
  font-weight: 500;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unread-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1rem;
  height: 1rem;
  padding: 0 0.25rem;
  font-size: 0.675rem;
  font-weight: 600;
  color: white;
  background-color: var(--color-error);
  border-radius: 9999px;
}

.loading-text {
  font-size: 0.875rem;
  color: var(--color-text-light);
}

.dark .loading-text {
  color: var(--color-text-dark);
}

.no-data-text {
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.value-text {
  font-weight: 600;
  color: var(--color-success);
}

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: var(--color-scrollbar) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: var(--color-scrollbar);
  border-radius: 3px;
}

.dark .custom-scrollbar {
  scrollbar-color: var(--color-scrollbar-dark) transparent;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: var(--color-scrollbar-dark);
}

:root {
  --color-background-light: #ffffff;
  --color-background-dark: #1e293b;
  --color-border-light: #e2e8f0;
  --color-border-dark: #334155;
  --color-heading-light: #475569;
  --color-heading-dark: #94a3b8;
  --color-body-light: #1e293b;
  --color-body-dark: #e2e8f0;
  --color-text-light: #64748b;
  --color-text-dark: #94a3b8;
  --color-text-muted: #64748b;
  --color-woot: #4f46e5;
  --color-error: #ef4444;
  --color-success: #10b981;
  --color-scrollbar: #94a3b8;
  --color-scrollbar-dark: #475569;
  --white: #ffffff;
  --color-warning: #f59e0b;
  --color-info: #6366f1;
  --color-card-gradient: rgba(255, 255, 255, 0.5);
  --color-card-gradient-darker: rgba(30, 41, 59, 0.5);
  --color-woot-rgb: 79, 70, 229;
  --color-success-rgb: 16, 185, 129;
  --color-warning-rgb: 245, 158, 11;
  --color-info-rgb: 99, 102, 241;
}

/* Adicione estes estilos */
.tab-content {
  transition: opacity 0.2s ease-in-out;
}

.tab-content-enter-active,
.tab-content-leave-active {
  transition: opacity 0.2s ease-in-out;
}

.tab-content-enter-from,
.tab-content-leave-to {
  opacity: 0;
}

.tabs-container {
  border-bottom: 1px solid var(--color-border-light);
  margin-bottom: 1rem;
}

.dark .tabs-container {
  border-color: var(--color-border-dark);
}

.tab-button {
  @apply px-4 py-2 text-sm font-medium relative transition-colors;

  &.tab-active {
    @apply text-n-brand;

    &::after {
      content: '';
      @apply absolute bottom-0 left-0 right-0 h-0.5 bg-n-brand;
    }
  }

  &.tab-inactive {
    @apply text-n-slate-11 hover:text-n-slate-12;
  }
}

.tab-inactive:hover {
  color: var(--color-text-light);
}

.dark .tab-inactive {
  color: var(--color-text-dark);
}

.dark .tab-inactive:hover {
  color: var(--color-text-light);
}

.tab-counter {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text-muted);
  background-color: var(--color-background-light);
  border-radius: 9999px;
}

.dark .tab-counter {
  background-color: var(--color-background-dark);
}

.tab-active .tab-counter {
  color: var(--color-woot);
  background-color: var(--color-woot-50);
}

.dark .tab-active .tab-counter {
  background-color: rgba(79, 70, 229, 0.1);
}

.note-attachments {
  @apply mt-4;
}

.action-button {
  @apply flex items-center justify-center w-10 h-10 rounded-lg
         text-n-slate-11 hover:text-n-slate-12
         bg-n-solid-3 hover:bg-n-alpha-1
         transition-all duration-200;

  &:disabled {
    @apply opacity-50 cursor-not-allowed;
  }
}

.primary-button {
  @apply px-4 py-2 text-sm font-medium text-white
         bg-n-brand hover:brightness-110
         rounded-lg transition-colors;

  &:disabled {
    @apply opacity-50 cursor-not-allowed;
  }
}

.notes-container {
  @apply flex flex-col gap-4;
}

.notes-container.with-preview {
  @apply grid grid-cols-2 gap-4;
}

.notes-content {
  @apply flex-1;
}

.images-preview {
  @apply space-y-4 border-l border-n-weak pl-4;
}

.image-preview-item {
  @apply rounded-lg overflow-hidden bg-n-solid-3;
}

.preview-image {
  @apply w-24 h-24 object-cover rounded-lg cursor-pointer 
         hover:opacity-90 transition-opacity;
}

.note-card {
  @apply bg-n-solid-2 rounded-lg p-4 mb-4
         border border-n-weak;
}

.note-layout {
  @apply flex justify-between gap-4;
}

.note-content {
  @apply flex-1 flex flex-col justify-between;
}

.note-text {
  @apply flex-1 text-n-slate-12 whitespace-pre-wrap mb-4;
}

.note-metadata {
  @apply flex items-center justify-between text-sm
         text-n-slate-11 mb-3;
}

.note-author {
  @apply font-medium;
}

.note-date {
  @apply text-xs;
}

.note-files {
  @apply mt-4 space-y-2 border-t border-n-weak pt-4;
}

.file-attachment {
  @apply flex items-center justify-between p-2
         bg-n-solid-3
         rounded-lg text-sm;
}

.file-info {
  @apply flex items-center gap-1.5 flex-1 min-w-0;
}

.file-icon {
  @apply flex-shrink-0 text-n-slate-10;
}

.file-name {
  @apply text-[10px] hover:text-n-brand
         text-n-slate-12 max-w-[150px]
         whitespace-nowrap;
}

.file-action {
  @apply p-1.5 text-n-slate-10
         rounded-md
         transition-colors;
}

.file-action:hover {
  color: rgb(var(--ruby-11));
  background-color: rgb(var(--ruby-3));
}

.note-images {
  @apply flex-shrink-0 flex flex-col gap-3 w-24;
}

.image-preview {
  @apply rounded-lg overflow-hidden bg-n-solid-3;
}

.preview-image {
  @apply w-24 h-24 object-cover rounded-lg cursor-pointer 
         hover:opacity-90 transition-opacity;
}

/* Quando não há imagens, o conteúdo ocupa todo o espaço */
.note-layout:not(:has(.note-images)) .note-content {
  @apply w-full;
}

/* Ajuste para telas menores */
@media (max-width: 640px) {
  .note-layout {
    @apply flex-col gap-3;
  }

  .note-images {
    @apply w-full flex-row flex-wrap;
  }

  .preview-image {
    @apply w-16 h-16;
  }
}

/* Adicione estes estilos para os botões de ação */
.note-actions {
  @apply flex items-center gap-1;
}

.note-action-button {
  @apply p-1 text-n-slate-10 transition-colors;
}

.note-action-button:hover {
  @apply text-n-slate-12;
}

.note-action-button.delete:hover {
  color: rgb(var(--ruby-11));
}

.icon-button {
  @apply flex items-center justify-center w-6 h-6 rounded-full 
         transition-colors duration-200;
}

.icon-button:hover {
  @apply bg-n-alpha-1;
}

.attachments-grid {
  @apply space-y-6;
}

.attachment-section {
  @apply space-y-3;
}

.section-title {
  @apply text-sm font-medium text-n-slate-12;
}

.images-grid {
  @apply grid grid-cols-8 gap-2;
}

.image-card {
  @apply rounded-md overflow-hidden bg-n-solid-3
         border border-n-weak;
}

.attachment-preview {
  @apply w-full h-16 object-cover cursor-pointer 
         hover:opacity-90 transition-opacity;
}

.attachment-info {
  @apply p-1 flex items-center justify-between;
}

.attachment-name {
  @apply text-[10px] text-n-slate-12 truncate max-w-[80px];
}

.files-grid {
  @apply space-y-2;
}

.file-card {
  @apply flex items-center justify-between p-2
          bg-n-solid-3 rounded-lg;
}

.delete-button {
  @apply p-1 text-n-slate-10
         rounded-md
         transition-colors;
}

.delete-button:hover {
  color: rgb(var(--ruby-11));
  background-color: rgb(var(--ruby-3));
}

.attachment-source,
.file-source {
  @apply text-[10px] text-n-slate-10 mt-0.5 truncate max-w-[80px];
}

.note-input-section {
  @apply space-y-2;
}

/* Adicione estes estilos */
.selected-file-preview {
  @apply mt-1 px-2 py-1 text-xs bg-n-solid-3
         rounded border border-n-weak;
}

.history-timeline {
  @apply relative mt-8;
}

.history-item {
  @apply relative pb-12 pl-16;
}

.history-connector {
  @apply absolute left-0 top-0 bottom-0 w-16;
}

.history-icon {
  @apply absolute left-0 z-10 flex items-center justify-center w-8 h-8
          rounded-full bg-n-solid-2 border-2 border-n-brand
          text-n-brand;
}

/* Adiciona a linha vertical */
.history-connector::after {
  content: '';
  @apply absolute left-4 top-8 bottom-0 w-0.5 -translate-x-1/2
          bg-n-brand;
}

/* Remove a linha do último item */
.history-item:last-child .history-connector::after {
  @apply hidden;
}

.history-content {
  @apply bg-n-solid-2 rounded-lg p-4
          border border-n-weak
          shadow-sm;
}

.history-header {
  @apply flex items-center justify-between mb-3;
}

.history-title {
  @apply text-sm font-medium text-n-slate-12;
}

.history-date {
  @apply text-xs text-n-slate-10 whitespace-nowrap;
}

.history-details {
  @apply text-sm text-n-slate-11 mb-3;
}

.history-author {
  @apply flex items-center gap-2 pt-3 mt-3 border-t
          border-n-weak;
}

.settings-modal {
  @apply flex flex-col;

  .settings-header {
    @apply p-4 border-b border-n-weak;
  }

  .settings-content {
    @apply p-4 space-y-4;
  }
}

/* Estilos adicionais para o bloco direito */
.quick-actions button:hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease;
}

/* Estilos adicionais para os botões de ação rápida */
.quick-action-btn {
  @apply flex items-center justify-center p-3 rounded-xl transition-all duration-200 bg-n-solid-3;
}

.quick-action-btn:hover {
  @apply transform -translate-y-0.5 shadow-lg;
}

.quick-action-btn:active {
  @apply transform translate-y-0 shadow-md;
}

/* Estilos para o Card de Estatísticas */
.item-statistics {
  /* Adicione estilos se necessário */
}
</style>
